import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { Account, AccountConfig, AccountType } from '../types'

const DEMO_BASE_URL = 'https://demo-api.ig.com/gateway/deal'
const PROD_BASE_URL = 'https://api.ig.com/gateway/deal'
const ACCOUNTS_STORAGE_KEY = 'ig_accounts'
const CURRENT_ACCOUNT_KEY = 'current_account_id'

interface AccountContextType {
  accounts: Account[]
  currentAccount: Account | null
  addAccount: (config: AccountConfig, token: string, user: any) => void
  removeAccount: (accountId: string) => void
  switchAccount: (accountId: string) => void
  updateAccountName: (accountId: string, name: string) => void
  getBaseUrlForType: (type: AccountType) => string
}

const AccountContext = createContext<AccountContextType | undefined>(undefined)

export const useAccounts = () => {
  const context = useContext(AccountContext)
  if (!context) {
    throw new Error('useAccounts must be used within an AccountProvider')
  }
  return context
}

interface AccountProviderProps {
  children: ReactNode
}

export const AccountProvider: React.FC<AccountProviderProps> = ({ children }) => {
  const [accounts, setAccounts] = useState<Account[]>([])
  const [currentAccount, setCurrentAccount] = useState<Account | null>(null)

  // Load accounts from localStorage on mount
  useEffect(() => {
    const storedAccounts = localStorage.getItem(ACCOUNTS_STORAGE_KEY)
    const currentAccountId = localStorage.getItem(CURRENT_ACCOUNT_KEY)

    if (storedAccounts) {
      try {
        const parsed: Account[] = JSON.parse(storedAccounts)
        setAccounts(parsed)

        if (currentAccountId) {
          const current = parsed.find(acc => acc.id === currentAccountId)
          if (current) {
            setCurrentAccount(current)
          } else if (parsed.length > 0) {
            setCurrentAccount(parsed[0])
          }
        } else if (parsed.length > 0) {
          setCurrentAccount(parsed[0])
        }
      } catch (error) {
        console.error('Failed to parse stored accounts:', error)
        localStorage.removeItem(ACCOUNTS_STORAGE_KEY)
      }
    }
  }, [])

  // Save accounts to localStorage whenever they change
  useEffect(() => {
    if (accounts.length > 0) {
      localStorage.setItem(ACCOUNTS_STORAGE_KEY, JSON.stringify(accounts))
    } else {
      localStorage.removeItem(ACCOUNTS_STORAGE_KEY)
    }
  }, [accounts])

  // Save current account ID whenever it changes
  useEffect(() => {
    if (currentAccount) {
      localStorage.setItem(CURRENT_ACCOUNT_KEY, currentAccount.id)
    } else {
      localStorage.removeItem(CURRENT_ACCOUNT_KEY)
    }
  }, [currentAccount])

  const getBaseUrlForType = (type: AccountType): string => {
    return type === 'DEMO' ? DEMO_BASE_URL : PROD_BASE_URL
  }

  const addAccount = (config: AccountConfig, token: string, user: any) => {
    // Check if account already exists (by username and type)
    const existingAccountIndex = accounts.findIndex(
      acc => acc.user?.username === user.username && acc.type === config.type
    )

    if (existingAccountIndex !== -1) {
      // Update existing account
      const updatedAccount: Account = {
        ...accounts[existingAccountIndex],
        name: config.name,
        token,
        user,
        lastUsed: new Date().toISOString()
      }

      setAccounts(prev => prev.map((acc, idx) =>
        idx === existingAccountIndex ? updatedAccount : acc
      ))
      setCurrentAccount(updatedAccount)
    } else {
      // Create new account
      const newAccount: Account = {
        id: `${config.type.toLowerCase()}_${user.username}_${Date.now()}`,
        name: config.name,
        type: config.type,
        baseUrl: getBaseUrlForType(config.type),
        token,
        user,
        createdAt: new Date().toISOString(),
        lastUsed: new Date().toISOString()
      }

      setAccounts(prev => [...prev, newAccount])
      setCurrentAccount(newAccount)
    }
  }

  const removeAccount = (accountId: string) => {
    setAccounts(prev => {
      const updated = prev.filter(acc => acc.id !== accountId)

      // If we're removing the current account, switch to another
      if (currentAccount?.id === accountId) {
        setCurrentAccount(updated.length > 0 ? updated[0] : null)
      }

      return updated
    })
  }

  const switchAccount = (accountId: string) => {
    const account = accounts.find(acc => acc.id === accountId)
    if (account) {
      const updatedAccount = {
        ...account,
        lastUsed: new Date().toISOString()
      }

      setAccounts(prev =>
        prev.map(acc => acc.id === accountId ? updatedAccount : acc)
      )
      setCurrentAccount(updatedAccount)
    }
  }

  const updateAccountName = (accountId: string, name: string) => {
    setAccounts(prev =>
      prev.map(acc => acc.id === accountId ? { ...acc, name } : acc)
    )

    if (currentAccount?.id === accountId) {
      setCurrentAccount(prev => prev ? { ...prev, name } : null)
    }
  }

  const value: AccountContextType = {
    accounts,
    currentAccount,
    addAccount,
    removeAccount,
    switchAccount,
    updateAccountName,
    getBaseUrlForType
  }

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>
}
